import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';

interface Company {
  id: string;
  name: string;
}

interface Campaign {
  id: string;
  name: string;
  dripify_names: string[];
  submitted_at?: string | null;
  list_name?: string;
}

interface EntryRow {
  campaign_id: string;
  campaign_name: string;
  dripify_names: string[];
  list_name: string;
  submitted_at: string | null;
  sends: string;
  accepts: string;
}

interface ParsedEntry {
  name: string;
  sends: number;
  accepts: number;
  responses: number;
  date: string | null;
}

interface MatchResult {
  parsed: ParsedEntry;
  matchedCampaignId: string | null;   // auto-matched via dripify_names[]
  manualCampaignId: string | null;    // admin-selected via dropdown
}

// ─── Parser ───────────────────────────────────────────────────────────────────

const SKIP_KEYWORDS = new Set([
  'all leads', 'lists of leads', 'acceptance rate', 'response rate',
  'dashboard', 'campaigns', 'inbox', 'leads', 'teams', 'settings',
  'preview mode', 'overview', 'linkedin', 'status', 'search', 'new campaign',
  'what\'s new', 'exit', 'skip guide',
]);

function looksLikeCampaignName(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('_')) return false;
  if (/^\d+$/.test(trimmed)) return false;
  if (/^\d+\.\d+%$/.test(trimmed)) return false;
  if (/^[A-Za-z]+ \d+, \d{4}$/.test(trimmed)) return false;
  if (/^\d+\/\d+/.test(trimmed)) return false;
  if (SKIP_KEYWORDS.has(trimmed.toLowerCase())) return false;
  return trimmed.length > 5;
}

function parseDripifyText(raw: string): ParsedEntry[] {
  const lines = raw.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const results: ParsedEntry[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!looksLikeCampaignName(lines[i])) { i++; continue; }

    const name = lines[i].trim();
    let accepts = 0, sends = 0, responses = 0;
    let date: string | null = null;
    let foundAcceptance = false;

    let j = i + 1;
    while (j < lines.length && j < i + 25) {
      const cur = lines[j];
      if (cur === 'Acceptance rate') {
        const pct = parseFloat((lines[j + 1] || '').replace('%', ''));
        const cnt = parseInt(lines[j + 2] || '', 10);
        if (!isNaN(pct) && pct > 0 && !isNaN(cnt)) {
          accepts = cnt;
          sends = Math.round(cnt / (pct / 100));
          foundAcceptance = true;
        }
        j += 3; continue;
      }
      if (cur === 'Response rate' || cur === 'Response rate ') {
        const cnt = parseInt(lines[j + 2] || '', 10);
        if (!isNaN(cnt)) responses = cnt;
        j += 3; continue;
      }
      if (/^[A-Za-z]+ \d+, \d{4}$/.test(cur)) { date = cur; j++; break; }
      if (looksLikeCampaignName(cur) && j > i + 3) break;
      j++;
    }

    if (foundAcceptance) results.push({ name, sends, accepts, responses, date });
    i = j;
  }
  return results;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMondayOfWeek(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function formatWeekLabel(monday: string): string {
  const start = new Date(monday + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatSubmitted(ts: string | null | undefined): string {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function findMatchingCampaign(dripifyName: string, rows: EntryRow[]): EntryRow | null {
  const dn = dripifyName.toLowerCase().trim();
  return rows.find(r =>
    r.campaign_name.toLowerCase().trim() === dn ||
    r.dripify_names.some(n => n.toLowerCase().trim() === dn)
  ) ?? null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AdminDripifyEntry: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [weekStart, setWeekStart] = useState(getMondayOfWeek(new Date()));
  const [rows, setRows] = useState<EntryRow[]>([]);
  const [existingImportId, setExistingImportId] = useState<string | null>(null);
  const [lastEntered, setLastEntered] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Paste section
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [matchResults, setMatchResults] = useState<MatchResult[] | null>(null);
  const [savingAliases, setSavingAliases] = useState(false);

  // Sheet sync
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    campaigns_updated: number;
    groups_configured: number;
    skipped_campaigns: string[];
    error?: string;
  } | null>(null);

  useEffect(() => {
    supabase.from('companies').select('id, name').order('name')
      .then(({ data }) => setCompanies(data || []));
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedCompanyId || !weekStart) return;
    setLoading(true);
    setSaveMessage(null);
    setMatchResults(null);

    const { data: campData } = await supabase
      .from('campaigns')
      .select('id, name, dripify_names, submitted_at')
      .eq('company_id', selectedCompanyId)
      .eq('status', 'running')
      .order('name');

    const campList: Campaign[] = (campData || []).map(c => ({
      ...c,
      dripify_names: c.dripify_names ?? [],
    }));

    const campIds = campList.map(c => c.id);
    let listNameMap: Record<string, string> = {};
    if (campIds.length > 0) {
      const { data: filterData } = await supabase
        .from('preset_filter_requests')
        .select('campaign_id, request_name')
        .in('campaign_id', campIds);
      (filterData || []).forEach((r: any) => { listNameMap[r.campaign_id] = r.request_name; });
    }

    const enriched: Campaign[] = campList.map(c => ({ ...c, list_name: listNameMap[c.id] || '' }));

    const { data: importData } = await supabase
      .from('dripify_imports')
      .select('id, entries, created_at')
      .eq('company_id', selectedCompanyId)
      .eq('week_start', weekStart)
      .maybeSingle();

    if (importData) {
      setExistingImportId(importData.id);
      setLastEntered(new Date(importData.created_at).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      }));
      const existingMap: Record<string, { sends: string; accepts: string }> = {};
      (importData.entries || []).forEach((e: any) => {
        existingMap[e.campaign_id] = { sends: String(e.sends ?? ''), accepts: String(e.accepts ?? '') };
      });
      setRows(enriched.map(c => ({
        campaign_id: c.id,
        campaign_name: c.name,
        dripify_names: c.dripify_names,
        list_name: c.list_name || '',
        submitted_at: c.submitted_at || null,
        sends: existingMap[c.id]?.sends ?? '',
        accepts: existingMap[c.id]?.accepts ?? '',
      })));
    } else {
      setExistingImportId(null);
      setLastEntered(null);
      setRows(enriched.map(c => ({
        campaign_id: c.id,
        campaign_name: c.name,
        dripify_names: c.dripify_names,
        list_name: c.list_name || '',
        submitted_at: c.submitted_at || null,
        sends: '',
        accepts: '',
      })));
    }
    setLoading(false);
  }, [selectedCompanyId, weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateRow = (idx: number, field: 'sends' | 'accepts', value: string) => {
    setRows(prev => prev.map((r, i) =>
      i === idx ? { ...r, [field]: value.replace(/[^0-9]/g, '') } : r
    ));
  };

  // ── Parse ────────────────────────────────────────────────────────────────────

  const handleParse = () => {
    const parsed = parseDripifyText(pasteText);
    if (parsed.length === 0) { setMatchResults([]); return; }
    const results: MatchResult[] = parsed.map(p => {
      const matched = findMatchingCampaign(p.name, rows);
      return { parsed: p, matchedCampaignId: matched?.campaign_id ?? null, manualCampaignId: null };
    });
    setMatchResults(results);
  };

  const setManualLink = (resultIdx: number, campaignId: string) => {
    setMatchResults(prev => prev
      ? prev.map((r, i) => i === resultIdx ? { ...r, manualCampaignId: campaignId || null } : r)
      : prev
    );
  };

  // ── Apply ────────────────────────────────────────────────────────────────────
  // Multiple Dripify campaigns can map to one SE campaign — sends/accepts are SUMMED.

  const handleApply = async () => {
    if (!matchResults) return;
    setSavingAliases(true);

    // 1. Persist new Dripify names for manually linked entries
    const manualLinks = matchResults.filter(m => !m.matchedCampaignId && m.manualCampaignId);
    for (const link of manualLinks) {
      const row = rows.find(r => r.campaign_id === link.manualCampaignId);
      if (!row) continue;
      const merged = Array.from(new Set([...row.dripify_names, link.parsed.name]));
      await supabase.from('campaigns').update({ dripify_names: merged }).eq('id', link.manualCampaignId!);
      // Update local row state so future matches work without reload
      setRows(prev => prev.map(r =>
        r.campaign_id === link.manualCampaignId ? { ...r, dripify_names: merged } : r
      ));
    }

    // 2. Aggregate sends + accepts per SE campaign (multiple Dripify → one SE)
    const totals = new Map<string, { sends: number; accepts: number }>();
    for (const m of matchResults) {
      const campaignId = m.matchedCampaignId ?? m.manualCampaignId;
      if (!campaignId) continue;
      const existing = totals.get(campaignId) ?? { sends: 0, accepts: 0 };
      totals.set(campaignId, {
        sends: existing.sends + m.parsed.sends,
        accepts: existing.accepts + m.parsed.accepts,
      });
    }

    setRows(prev => prev.map(row => {
      const agg = totals.get(row.campaign_id);
      return agg ? { ...row, sends: String(agg.sends), accepts: String(agg.accepts) } : row;
    }));

    setSavingAliases(false);
    setPasteOpen(false);
    setPasteText('');
    setMatchResults(null);

    if (manualLinks.length > 0) {
      setSaveMessage({
        type: 'success',
        text: `Linked ${manualLinks.length} new Dripify name${manualLinks.length > 1 ? 's' : ''} — they'll auto-match on future pastes.`,
      });
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  // ── Sheet sync ───────────────────────────────────────────────────────────────

  const handleSheetSync = async () => {
    if (!selectedCompanyId) return;
    setSyncing(true);
    setSyncResult(null);

    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-sheet-campaigns`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ company_id: selectedCompanyId, company_name: selectedCompanyName }),
      }
    );

    const json = await res.json();
    if (!res.ok) {
      setSyncResult({ campaigns_updated: 0, groups_configured: 0, skipped_campaigns: [], error: json.error ?? 'Sync failed' });
    } else {
      setSyncResult(json);
      loadData(); // Reload so dripify_names show immediately
    }
    setSyncing(false);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedCompanyId) return;
    setSaving(true);
    setSaveMessage(null);

    const { data: { user } } = await supabase.auth.getUser();
    const entries = rows.map(r => ({
      campaign_id: r.campaign_id,
      campaign_name: r.campaign_name,
      list_name: r.list_name,
      sends: parseInt(r.sends || '0', 10),
      accepts: parseInt(r.accepts || '0', 10),
    }));

    let error;
    if (existingImportId) {
      ({ error } = await supabase.from('dripify_imports').update({ entries, imported_by: user?.id }).eq('id', existingImportId));
    } else {
      ({ error } = await supabase.from('dripify_imports').insert({ company_id: selectedCompanyId, imported_by: user?.id, week_start: weekStart, entries }));
    }

    if (error) {
      setSaveMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } else {
      setSaveMessage({ type: 'success', text: 'Saved successfully.' });
      loadData();
    }
    setSaving(false);
  };

  // ── Derived counts ────────────────────────────────────────────────────────────

  const selectedCompanyName = companies.find(c => c.id === selectedCompanyId)?.name || '';

  // Count unique SE campaigns that will receive data (after aggregation)
  const resolvedCampaignIds = matchResults
    ? new Set(matchResults.map(m => m.matchedCampaignId ?? m.manualCampaignId).filter(Boolean))
    : new Set();
  const totalApplicable = resolvedCampaignIds.size;
  const manualLinkedCount = matchResults?.filter(m => !m.matchedCampaignId && m.manualCampaignId).length ?? 0;
  const unmatchedCount = matchResults?.filter(m => !m.matchedCampaignId && !m.manualCampaignId).length ?? 0;

  // Per SE campaign, how many Dripify entries aggregate into it
  const aggregationCount = new Map<string, number>();
  if (matchResults) {
    for (const m of matchResults) {
      const id = m.matchedCampaignId ?? m.manualCampaignId;
      if (id) aggregationCount.set(id, (aggregationCount.get(id) ?? 0) + 1);
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Dripify Data Entry</h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter sends and accepts from Dripify for each running campaign. Used by the weekly report pipeline.
      </p>

      {/* Company + week controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Week of{weekStart ? ` (${formatWeekLabel(weekStart)})` : ''}
          </label>
          <input
            type="date"
            value={weekStart}
            onChange={e => setWeekStart(getMondayOfWeek(new Date(e.target.value + 'T00:00:00')))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">Automatically snapped to Monday of selected week.</p>
        </div>
      </div>

      {/* Sheet sync */}
      {selectedCompanyId && (
        <div className="mb-5 flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Sync from Google Sheet</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Sets Dripify aliases, campaign goals, and report groups for {selectedCompanyName} from the master sheet.
            </p>
          </div>
          <button
            onClick={handleSheetSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 shrink-0"
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Syncing…
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync from Sheet
              </>
            )}
          </button>
        </div>
      )}

      {syncResult && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${syncResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {syncResult.error ? (
            <p>Sync failed: {syncResult.error}</p>
          ) : (
            <>
              <p className="font-medium">
                ✓ {syncResult.campaigns_updated} campaign{syncResult.campaigns_updated !== 1 ? 's' : ''} updated
                {syncResult.groups_configured > 0 && ` · ${syncResult.groups_configured} new report group${syncResult.groups_configured !== 1 ? 's' : ''} created`}
              </p>
              {syncResult.skipped_campaigns.length > 0 && (
                <p className="mt-1 text-xs opacity-75">
                  Skipped (no matching SE campaign): {syncResult.skipped_campaigns.join(', ')}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {lastEntered && (
        <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          Data already entered for this week (last saved: {lastEntered}). You can update it below.
        </div>
      )}

      {selectedCompanyId && !loading && rows.length > 0 && (
        <>
          {/* Paste from Dripify */}
          <div className="mb-5 border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => { setPasteOpen(o => !o); setMatchResults(null); }}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
            >
              <span>📋 Paste from Dripify</span>
              <span className="text-gray-400 text-xs">{pasteOpen ? '▲ hide' : '▼ expand'}</span>
            </button>

            {pasteOpen && (
              <div className="p-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">
                  Select all text on the Dripify campaigns page (Ctrl+A → Ctrl+C) and paste below.
                  Campaigns with saved aliases auto-match. Multiple Dripify campaigns mapping to the same SE campaign are aggregated automatically.
                </p>
                <textarea
                  value={pasteText}
                  onChange={e => { setPasteText(e.target.value); setMatchResults(null); }}
                  placeholder="Paste Dripify page text here..."
                  rows={8}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                />
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleParse}
                    disabled={!pasteText.trim()}
                    className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40"
                  >
                    Parse
                  </button>
                  {matchResults && matchResults.length > 0 && (
                    <button
                      onClick={handleApply}
                      disabled={totalApplicable === 0 || savingAliases}
                      className="px-4 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-40"
                    >
                      {savingAliases
                        ? 'Saving…'
                        : `Apply → ${totalApplicable} SE campaign${totalApplicable !== 1 ? 's' : ''}${manualLinkedCount > 0 ? ` + save ${manualLinkedCount} alias${manualLinkedCount !== 1 ? 'es' : ''}` : ''}`}
                    </button>
                  )}
                  {matchResults !== null && (
                    <span className="text-xs text-gray-500">
                      {matchResults.length === 0
                        ? 'No campaigns detected.'
                        : `${matchResults.length} Dripify campaigns → ${totalApplicable} SE campaign${totalApplicable !== 1 ? 's' : ''} · ${unmatchedCount} unlinked`}
                    </span>
                  )}
                </div>

                {matchResults && matchResults.length > 0 && (
                  <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Dripify Campaign</th>
                          <th className="text-center px-3 py-2 font-medium text-gray-600 w-14">Sends</th>
                          <th className="text-center px-3 py-2 font-medium text-gray-600 w-14">Accepts</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">SE Campaign (aggregated)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchResults.map((r, i) => {
                          const isAutoMatched = !!r.matchedCampaignId;
                          const isManualLinked = !isAutoMatched && !!r.manualCampaignId;
                          const resolvedId = r.matchedCampaignId ?? r.manualCampaignId;
                          const matchedRow = resolvedId ? rows.find(row => row.campaign_id === resolvedId) : null;
                          const aggCount = resolvedId ? (aggregationCount.get(resolvedId) ?? 1) : 1;

                          return (
                            <tr key={i} className={`border-b border-gray-100 ${isAutoMatched ? '' : isManualLinked ? 'bg-blue-50' : 'bg-amber-50'}`}>
                              <td className="px-3 py-2 font-mono text-gray-700 max-w-xs">
                                <div className="truncate">{r.parsed.name}</div>
                              </td>
                              <td className="px-3 py-2 text-center text-gray-700">{r.parsed.sends}</td>
                              <td className="px-3 py-2 text-center text-gray-700">{r.parsed.accepts}</td>
                              <td className="px-3 py-2">
                                {isAutoMatched || isManualLinked ? (
                                  <div>
                                    <span className={`font-medium ${isAutoMatched ? 'text-green-700' : 'text-blue-700'}`}>
                                      {isAutoMatched ? '✓' : '→'} {matchedRow?.campaign_name}
                                    </span>
                                    {aggCount > 1 && (
                                      <span className="ml-2 text-gray-400 text-xs">{aggCount} Dripify → 1 total</span>
                                    )}
                                    {isManualLinked && (
                                      <span className="ml-2 text-blue-500 text-xs">💾 alias saved</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={r.manualCampaignId ?? ''}
                                      onChange={e => setManualLink(i, e.target.value)}
                                      className="flex-1 border border-amber-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-400 bg-white text-gray-600"
                                    >
                                      <option value="">— link to SE campaign —</option>
                                      {rows.map(row => (
                                        <option key={row.campaign_id} value={row.campaign_id}>
                                          {row.campaign_name}
                                        </option>
                                      ))}
                                    </select>
                                    <span className="text-amber-500 text-xs shrink-0">unlinked</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {unmatchedCount > 0 && (
                      <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700">
                        Unlinked campaigns won't be applied. Link them above or sync from the Google Sheet to add them automatically.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Campaign table */}
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-700">Campaign · Target List</th>
                <th className="text-center py-2 px-3 font-medium text-gray-700 w-24">Sends</th>
                <th className="text-center py-2 pl-3 font-medium text-gray-700 w-24">Accepts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${row.campaign_id}-${idx}`} className="border-b border-gray-100">
                  <td className="py-2 pr-4">
                    <div className="text-gray-800 font-medium">{row.campaign_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5 flex flex-wrap gap-x-1">
                      {row.list_name && <span>📋 {row.list_name}</span>}
                      {row.list_name && row.submitted_at && <span>·</span>}
                      {row.submitted_at && <span>submitted {formatSubmitted(row.submitted_at)}</span>}
                      {row.dripify_names.length > 0 && (
                        <>
                          {(row.list_name || row.submitted_at) && <span>·</span>}
                          <span className="text-blue-400">
                            {row.dripify_names.length} Dripify alias{row.dripify_names.length !== 1 ? 'es' : ''}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text" inputMode="numeric" value={row.sends}
                      onChange={e => updateRow(idx, 'sends', e.target.value)}
                      placeholder="0"
                      className="w-full text-center border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </td>
                  <td className="py-2 pl-3">
                    <input
                      type="text" inputMode="numeric" value={row.accepts}
                      onChange={e => updateRow(idx, 'accepts', e.target.value)}
                      placeholder="0"
                      className="w-full text-center border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : existingImportId ? 'Update' : 'Save'}
            </button>
            {saveMessage && (
              <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {saveMessage.text}
              </span>
            )}
          </div>
        </>
      )}

      {selectedCompanyId && loading && (
        <div className="text-sm text-gray-500 py-8 text-center">Loading campaigns...</div>
      )}

      {selectedCompanyId && !loading && rows.length === 0 && (
        <div className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-200 rounded-lg">
          No running campaigns found for {selectedCompanyName}.
        </div>
      )}
    </div>
  );
};

export default AdminDripifyEntry;
